/**
 * `buildNextSteps` — the lead's right panel as a list of NEXT STEPS (#1921,
 * R3 of `docs/agent-prompts/handover-right-panel-and-inbox-2026-10-02.md`).
 *
 * **Why the lead opens the panel:** to know what still needs them on this
 * roster without reading the grid. **What they see first:** the one most
 * urgent thing, as a sentence with a count. **What they do straight away:**
 * click the row and land where the action is (the day's ladder, the review
 * queue, the swaps board, `Publish updates`).
 *
 * Pure (no React, no I/O): `useNextSteps` gathers the facts from the same
 * query hooks the existing surfaces use and hands them here, so the order,
 * the wording and the links are unit-tested on their own
 * (`web/tests/nextSteps.test.ts`).
 *
 * **The rules (handover §3).**
 *
 *   - One row per KIND, never one per item; a row exists only when its
 *     count is above zero.
 *   - The order is the fixed urgency order of `NEXT_STEP_ORDER` — it never
 *     reshuffles (no recency sort), so a row never jumps while the panel is
 *     open.
 *   - Rows are sentences in the app's own voice: each one reuses the words
 *     of the surface it points at (the coverage summary, the review queue,
 *     the swaps board's *Needs your confirm*, the toolbar's *unpublished
 *     edit*, the cycle header line, *Fixed placements that can't happen*,
 *     the team page's waiting-calls door, the submissions count).
 *   - #2193 — "N fixed-shift asks to answer" on either scope, opening the
 *     lead's own Inbox, where each ask is a card with Approve / Decline.
 *   - #2077 — "N messages from members" (Message your lead) on either
 *     scope, opening the People page's messages list; who sent them is
 *     its quieter second line (#2071).
 *   - Validation is not a row: its *N issues to review* folds into the
 *     short-days row as a second link (`aside`).
 *
 * `to` is always a router path; `focus` (optional) is a CSS selector the
 * list brings into view and focuses after navigating — for the actions
 * that are a control on the page rather than a page of their own (the
 * Publish button, the fixed-placements section, the review queue card).
 */
import {
  RELEASE_STATE_LINE,
  type RosterReleaseState,
  draftsHeldLine,
  resolveHeldLine,
} from "../../lib/cycleHeaderLine";
import { weekdayShortLabel } from "../../lib/dateWindow";
import { waitingCallsDates } from "../../lib/waitingCalls";
import { waitingChangesLine } from "../../lib/waitingChanges";

/** The kinds, in urgency order. */
export const NEXT_STEP_ORDER = [
  "short",
  "leave",
  "swaps",
  "asks",
  "unpublished",
  "release",
  "pins",
  "messages",
  "calls",
  "no_period",
  "submissions",
] as const;

export type NextStepKind = (typeof NEXT_STEP_ORDER)[number];

export interface NextStepRow {
  kind: NextStepKind;
  /** How many items the row stands for (1 for a one-off state). */
  count: number;
  /** The sentence — carries its own count. */
  line: string;
  /** A quieter second line (the days, the first pin, the dates). */
  secondary?: string;
  /** A second, smaller link under the row — validation's
   *  *N issues to review* on the short-days row. */
  aside?: { line: string; to: string; focus?: string };
  /** Router path the row opens. */
  to: string;
  /** In-page target (CSS selector) brought into view after navigating. */
  focus?: string;
}

/** What `useNextSteps` read. Every fact is optional: an absent fact (not
 *  read on this route, not the lead's, still loading, errored) makes no row. */
export interface NextStepsFacts {
  /** `cycle` on `/cycles/:id*`, `team` on `/teams/:id*`. */
  scope: "cycle" | "team";
  cycleId?: string | undefined;
  teamId?: string | undefined;
  /** Published days with an empty seat (the roster read's
   *  `shortfalls_by_day`, headcount incl. pooled floors — flows finding 43),
   *  as ISO dates. */
  shortDays?: readonly string[] | undefined;
  /** Validation's issue count — only read while the panel is open. */
  validationIssues?: number | undefined;
  /** The review queue: requests not looked at yet, and how many of those
   *  are urgent. `cycleId` is the period the queue was read for. */
  leave?: { toReview: number; urgent: number; cycleId: string } | undefined;
  /** Swaps held for the lead's confirm (`state_summary.accepted`). */
  swapsHeld?: number | undefined;
  /** #2193 — members' fixed-shift asks waiting for a lead, and the first. */
  fixedAsks?: { count: number; first?: { name: string; date: string } | undefined } | undefined;
  /** The count behind `Publish updates (N)` (published rosters only). */
  unpublishedEdits?: number | undefined;
  /** The release step the cycle header line says is owed. */
  release?: RosterReleaseState | undefined;
  /** #2027 — why a held roster is held (0 = for review; N = the check's
   *  hard-rule problems). */
  heldRuleProblems?: number | undefined;
  /** Fixed placements that can't happen: open seats + the first one. */
  pins?: { open: number; first?: { date: string; slot: string } | undefined } | undefined;
  /** #2077 — unread messages from members (Message your lead). */
  unreadMessages?: number | undefined;
  /** #2071 — who sent the unread messages, newest first, each name once. */
  messageSenders?: readonly string[] | undefined;
  /** Imported calls with no roster period yet (team routes). */
  waitingCalls?: { count: number; first: string; last: string } | undefined;
  /** The team has no roster period at all and nothing is waiting (team routes). */
  noPeriod?: boolean | undefined;
  /** Submissions — present only when this period collects them. */
  submissions?: { submitted: number; total: number; deadlineDay?: string | null } | undefined;
}

/** Selectors the list focuses after navigating. */
export const NEXT_STEP_FOCUS = {
  publish: '[data-testid="roster-publish-trigger"]',
  /** #2974 — the list inside the Off and fixed panel (`?surface=offfixed`). */
  pins: '[data-testid="allocation-gaps-section"]',
  validation: "#validation-heading",
  reviewQueue: '[data-testid="team-home-date-requests-card"]',
  waitingCalls: '[data-testid="team-waiting-calls"] [data-testid="team-open-waiting-dates"]',
  /** The home status line's one action: "Set up your team" on a team with
   *  no roster this week, "Open roster" otherwise (never both). */
  openRoster: '[data-testid="team-home-setup"], [data-testid="team-open-roster"]',
} as const;

/** The empty state, per scope. */
export const NEXT_STEPS_EMPTY = {
  cycle: "Nothing needs you on this roster.",
  team: "Nothing needs you on this team.",
} as const;

/** The link builders, shared with the team home's Needs you list (#2834) so
 *  both lists open the same screens. */
export const nextStepLinks = {
  /** A day's call-back ladder on the team's roster page. */
  ladder: (rosterPath: string, date: string) => `${rosterPath}?date=${date}&ladder=1`,
  /** Leave review for one window. */
  leaveReview: (teamPath: string, cycleId: string) => `${teamPath}?review=${cycleId}`,
  /** The swaps board, filtered to swaps held for the lead. */
  swapsHeld: (rosterPath: string) => `${rosterPath}?surface=swaps&swaps=accepted`,
  /** The lead's Inbox, where each fixed-shift ask is a card. */
  asks: () => "/me/inbox",
  /** The People page's messages list, unread first. */
  messages: (teamPath: string) => `${teamPath}/members?messages=unread`,
} as const;

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/** "From Ari" · "From Ari and Bo" · "From Ari, Bo and 2 more" (#2071). */
function sendersLine(names: readonly string[]): string {
  const [first, second] = names;
  if (names.length === 1) return `From ${first}`;
  if (names.length === 2) return `From ${first} and ${second}`;
  return `From ${first}, ${second} and ${names.length - 2} more`;
}

/** "Tue 3 Nov" · "Tue 3 Nov and Wed 4 Nov" · "Tue 3 Nov and 4 more days". */
function firstAndRest(days: readonly string[]): string {
  const [first, second] = days;
  if (!first) return "";
  if (days.length === 1) return weekdayShortLabel(first);
  if (days.length === 2 && second) {
    return `${weekdayShortLabel(first)} and ${weekdayShortLabel(second)}`;
  }
  return `${weekdayShortLabel(first)} and ${days.length - 1} more days`;
}

export function buildNextSteps(facts: NextStepsFacts): NextStepRow[] {
  const { cycleId, teamId, scope } = facts;
  // Roster rows live on a schedule's pages; the team-level rows (calls
  // waiting, no period yet) on the team's. Leave requests are both: the
  // schedule's own queue, or on the team page the queue's default period.
  const roster = scope === "cycle" && cycleId ? `/cycles/${cycleId}/roster` : null;
  const team = teamId ? `/teams/${teamId}` : null;
  const teamOnly = scope === "team" ? team : null;
  const byKind = new Map<NextStepKind, NextStepRow>();

  // 1. Short seats on a published roster → the first short day's ladder.
  const shortDays = [...(facts.shortDays ?? [])].sort();
  const firstShort = shortDays[0];
  if (roster && firstShort) {
    const n = shortDays.length;
    const issues = facts.validationIssues ?? 0;
    byKind.set("short", {
      kind: "short",
      count: n,
      line: `${n} ${plural(n, "day", "days")} short on published days`,
      secondary: firstAndRest(shortDays),
      to: nextStepLinks.ladder(roster, firstShort),
      ...(issues > 0
        ? {
            aside: {
              line: `${issues} ${plural(issues, "issue", "issues")} to review`,
              to: roster,
              focus: NEXT_STEP_FOCUS.validation,
            },
          }
        : {}),
    });
  }

  // 2. Leave requests not looked at → the team page's review queue, that period.
  const leave = facts.leave;
  if (team && leave && leave.toReview > 0) {
    const n = leave.toReview;
    byKind.set("leave", {
      kind: "leave",
      count: n,
      line: `${n} leave ${plural(n, "request", "requests")} to review${
        leave.urgent > 0 ? ` · ${leave.urgent} urgent` : ""
      }`,
      to: nextStepLinks.leaveReview(team, leave.cycleId),
      focus: NEXT_STEP_FOCUS.reviewQueue,
    });
  }

  // 3. Swaps held for the lead's confirm → the Swaps board, filtered to them.
  const held = facts.swapsHeld ?? 0;
  if (roster && held > 0) {
    byKind.set("swaps", {
      kind: "swaps",
      count: held,
      line: `${held} ${plural(held, "swap needs", "swaps need")} your confirm`,
      to: nextStepLinks.swapsHeld(roster),
    });
  }

  // 4. Unpublished edits → Publish updates (N).
  const edits = facts.unpublishedEdits ?? 0;
  if (roster && edits > 0) {
    byKind.set("unpublished", {
      kind: "unpublished",
      count: edits,
      // #2186 — members keep the published roster until Publish.
      line: waitingChangesLine(edits),
      to: roster,
      focus: NEXT_STEP_FOCUS.publish,
    });
  }

  // 5. The release step the header line says is owed → Publish to members /
  //    Publish roster (the same button).
  if (
    roster &&
    (facts.release === "published_untold" ||
      facts.release === "drafts_held" ||
      facts.release === "resolve_held")
  ) {
    const problems = { roster_held_rule_problems: facts.heldRuleProblems ?? 0 };
    byKind.set("release", {
      kind: "release",
      count: 1,
      line:
        facts.release === "published_untold"
          ? RELEASE_STATE_LINE.publishedUntold
          : facts.release === "resolve_held"
            ? resolveHeldLine(problems)
            : draftsHeldLine(problems),
      to: roster,
      focus: NEXT_STEP_FOCUS.publish,
    });
  }

  // 6. Fixed placements that can't happen → the Off and fixed panel, which
  //    holds that list since #2974 (the section left the roster page).
  const pins = facts.pins;
  if (roster && pins && pins.open > 0) {
    const n = pins.open;
    const where = pins.first ? `${weekdayShortLabel(pins.first.date)} · ${pins.first.slot}` : null;
    const head = `${n} ${plural(n, "pin can't", "pins can't")} be placed`;
    byKind.set("pins", {
      kind: "pins",
      count: n,
      line: n === 1 && where ? `${head} · ${where}` : head,
      ...(n > 1 && where ? { secondary: `${where} and ${n - 1} more` } : {}),
      to: `${roster}?surface=offfixed`,
      focus: NEXT_STEP_FOCUS.pins,
    });
  }

  // 3b. #2193 — members' fixed-shift asks waiting for a lead → the Inbox,
  //     where each one is a card with Approve / Decline. Both scopes.
  const asks = facts.fixedAsks;
  if (team && asks && asks.count > 0) {
    const n = asks.count;
    const first = asks.first ? `${asks.first.name} · ${weekdayShortLabel(asks.first.date)}` : null;
    byKind.set("asks", {
      kind: "asks",
      count: n,
      line: `${n} fixed-shift ${plural(n, "ask", "asks")} to answer`,
      ...(first ? { secondary: n > 1 ? `${first} and ${n - 1} more` : first } : {}),
      to: nextStepLinks.asks(),
    });
  }

  // 6b. Messages from members not read yet (#2077) → the People page's
  //     messages list, unread first. Both scopes: a message is the team's.
  const unread = facts.unreadMessages ?? 0;
  if (team && unread > 0) {
    byKind.set("messages", {
      kind: "messages",
      count: unread,
      line: `${unread} ${plural(unread, "message", "messages")} from members`,
      ...(facts.messageSenders?.length ? { secondary: sendersLine(facts.messageSenders) } : {}),
      to: nextStepLinks.messages(team),
    });
  }

  // 7. Imported calls on dates nobody has solved → the team page's door
  //    (its Open these dates opens the roster at them; #1959).
  const calls = facts.waitingCalls;
  if (teamOnly && calls && calls.count > 0) {
    const n = calls.count;
    byKind.set("calls", {
      kind: "calls",
      count: n,
      line: `${n} imported ${plural(n, "call waits", "calls wait")} on unsolved dates`,
      secondary: waitingCallsDates(calls),
      to: teamOnly,
      focus: NEXT_STEP_FOCUS.waitingCalls,
    });
  }

  // 7b. Nothing solved at all, nothing waiting → the home status line's
  //     action (#2048: "Set up your team" on a team with no roster; the
  //     roster is where a range of dates is solved, #1959).
  if (teamOnly && facts.noPeriod && !byKind.has("calls")) {
    byKind.set("no_period", {
      kind: "no_period",
      count: 1,
      line: "Nothing solved yet",
      secondary: "Set up your team, then solve a range of dates.",
      to: teamOnly,
      focus: NEXT_STEP_FOCUS.openRoster,
    });
  }

  // 8. Submissions still to come — only when this period collects them.
  const subs = facts.submissions;
  if (roster && subs && subs.total > subs.submitted) {
    byKind.set("submissions", {
      kind: "submissions",
      count: subs.total - subs.submitted,
      line: `${subs.submitted} of ${subs.total} submitted${
        subs.deadlineDay ? ` · closes ${weekdayShortLabel(subs.deadlineDay)}` : ""
      }`,
      to: `${roster}?surface=submissions`,
    });
  }

  return NEXT_STEP_ORDER.flatMap((kind) => {
    const row = byKind.get(kind);
    return row ? [row] : [];
  });
}
